// Split out of src/components/Onboarding.jsx (docs/FILE_SPLIT_RUN1_SPEC.md, move-only).
// Onboarding.jsx re-exports everything it exported before, so existing imports keep working.

import { Icon, ChildMark } from "../Common.jsx";
import { adultLabel } from "../../lib/adults.js";
import { t as ct } from "../../lib/content.js";

/* ---------------------- device kids setup flow ------------------------ */

// Three screens shown once, only for a device that just JOINED an established household
// (via household code + passcode, never the creator's own device) that already had more
// than one child at join time — see Root.jsx's handleJoin (sets the pending flag) and
// App.jsx (reads/clears it at mount, then drives which of the three screens is showing).
// A single-child household has nothing to choose between, so App.jsx never starts this
// flow in that case.

// Screen 1 — explains the feature, no PIN needed yet. "Not now" exits straight to Kid
// Mode with everything left at its default (every child shown, every child notified).
// Screen 1 of device setup, rewritten (Alan, 30 Aug 2026). It used to explain per-device
// visibility and then send the parent to a PIN pad and six toggles. Two problems with that:
// the PIN lands on whoever is holding the device, which after a quick-join QR is often the
// child, and their only way past it was a skip that left every sibling visible — the least
// private outcome reached by the most likely route. And it never ran at all on one-child
// households, where "which kids show here" has no content but "whose device is this" very
// much does.
//
// So the question changed rather than the timing. One tap, answerable by a child without
// the PIN, and everything else is derived from it: who is visible, who is muted, what the
// device opens into. The old per-child screen is still here, one link away, for the
// households these four answers don't describe.
export const DEVICE_ROLE_OPTIONS_NOTE = "kind: child | shared | parent";

// LB-312: the grown-up option is one row PER ADULT, not a yes/no. A household can have four
// adults now, each with their own PIN, and "A grown-up's device" could not say which of them
// — so a device set up this way opened Parent Mode asking for the Owner's PIN even when it
// belonged to somebody else. Naming the adult is what lets adultForDevice ask for theirs.
// (A device that arrived on an Add-a-device ticket never reaches this screen at all: F1
// asked the same question of the adult issuing the code, and asking twice is asking once too
// often — see Root.jsx's redeem path.)
export function DeviceWhoseScreen({ profiles, adults = [], onChoose, onIndividual, onSkip }) {
  const one = profiles.length === 1;
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-sm p-6 max-h-[90vh] overflow-y-auto" data-testid="device-whose">
        <div className="text-center mb-4">
          <div className="mb-1 flex justify-center"><Icon id="ic-devices" size={32} /></div>
          <div className="text-xl font-extrabold text-slate-900">{ct("onboarding.devicewhose.heading", "Whose device is this?")}</div>
          <p className="text-xs text-slate-500 leading-relaxed mt-1">
            {one
              ? ct("onboarding.devicewhose.body_one", "It decides what this device opens into and who gets alerts here. Parent Mode still works the same, whatever you pick.")
              : ct("onboarding.devicewhose.body_many", "It decides who shows up here and who gets alerts. Parent Mode still reaches every child, whatever you pick.")}
          </p>
        </div>

        <div className="flex flex-col gap-2 mb-3">
          {profiles.map((p) => (
            <DeviceWhoseOption
              key={p.id}
              testId={`device-whose-child-${p.id}`}
              icon={<ChildMark profile={p} size="sm" />}
              title={ct("onboarding.devicewhose.child_title", "{name}'s device", { name: p.name })}
              body={one
                ? ct("onboarding.devicewhose.child_body_one", "Opens in Kid Mode. {name} can turn on their own reminders.", { name: p.name })
                : ct("onboarding.devicewhose.child_body_many", "Only {name} shows here. Best for a device that stays with one child.", { name: p.name })}
              onClick={() => onChoose({ kind: "child", childId: p.id })}
            />
          ))}
          <DeviceWhoseOption
            testId="device-whose-shared"
            icon={<Icon id="ic-kids" size={20} hue="indigo" />}
            title={ct("onboarding.devicewhose.shared_title", "A shared family device")}
            body={one
              ? ct("onboarding.devicewhose.shared_body_one", "Opens in Kid Mode, as it does today.")
              : ct("onboarding.devicewhose.shared_body_many", "Everyone shows here. The tablet on the kitchen side.")}
            onClick={() => onChoose({ kind: "shared" })}
          />
          {adults.map((ad) => (
            <DeviceWhoseOption
              key={ad.id}
              testId={`device-whose-adult-${ad.id}`}
              icon={<Icon id="ic-lock" size={20} hue="indigo" />}
              title={ct("onboarding.devicewhose.adult_title", "{name}'s device", { name: adultLabel(ad) })}
              body={ct("onboarding.devicewhose.adult_body", "Opens in Parent Mode and asks for {name}'s own PIN. We'll offer request alerts here.", { name: adultLabel(ad) })}
              onClick={() => onChoose({ kind: "parent", adultId: ad.id })}
            />
          ))}
        </div>

        {/* Only offered where it has something to say: with one child this screen is two
            toggles, both of which the confirmation below already shows. */}
        {!one && (
          <button className="w-full text-slate-400 font-semibold text-xs py-1.5" data-testid="device-whose-individual" onClick={onIndividual}>
            {ct("onboarding.devicewhose.individual", "Choose child by child instead")}
          </button>
        )}
        <button className="w-full text-slate-500 font-semibold text-sm py-2" data-testid="device-whose-skip" onClick={onSkip}>
          {ct("onboarding.devicewhose.skip", "Not now, take me to Kid Mode")}
        </button>
      </div>
    </div>
  );
}

function DeviceWhoseOption({ icon, title, body, onClick, testId }) {
  return (
    <button type="button" data-testid={testId} onClick={onClick}
      className="w-full text-left flex items-start gap-3 bg-slate-50 border border-slate-100 rounded-2xl p-3 active:bg-slate-100">
      <span className="flex-shrink-0 w-7 flex justify-center pt-0.5">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-slate-800">{title}</span>
        <span className="block text-xs text-slate-500 leading-snug mt-0.5">{body}</span>
      </span>
    </button>
  );
}

// Screen 2 — what the answer means, before it takes effect anywhere.
//
// The alerts row is the one to read carefully. Nothing in this flow subscribes to push:
// hidden/muted are per-device UI state, and the only thing that ever creates a subscription
// is requestPushSubscription. ParentNotificationPrompt is already held back until this flow
// closes (see App.jsx's render gate), so it asks a moment later — which is right, because a
// refused browser permission is close to unrecoverable and deserves its own screen rather
// than riding along inside a summary. Hence "we'll ask you next" rather than "on".
export function DeviceRoleConfirmScreen({ profiles, adults = [], role, onConfirm, onBack }) {
  const child = role.kind === "child" ? profiles.find((p) => p.id === role.childId) : null;
  const isParent = role.kind === "parent";
  const adult = isParent && role.adultId ? adults.find((a) => a && a.id === role.adultId) : null;
  const title = adult
    ? ct("onboarding.deviceconfirm.title_adult", "Set up as {name}'s device", { name: adultLabel(adult) })
    : isParent
    ? ct("onboarding.deviceconfirm.title_parent", "Set up as a grown-up's device")
    : child
      ? ct("onboarding.deviceconfirm.title_child", "Set up as {name}'s device", { name: child.name })
      : ct("onboarding.deviceconfirm.title_shared", "Set up as a shared device");

  const rows = [];
  if (profiles.length > 1) {
    profiles.forEach((p) => {
      const visible = isParent || !child || p.id === child.id;
      rows.push({ key: `vis-${p.id}`, label: ct("onboarding.deviceconfirm.row_show", "Show {name}", { name: p.name }), value: visible ? "Yes" : "No", dim: !visible });
    });
  }
  rows.push({
    key: "mode",
    label: ct("onboarding.deviceconfirm.row_opens", "Opens in"),
    value: isParent ? ct("onboarding.deviceconfirm.opens_parent", "Parent Mode") : ct("onboarding.deviceconfirm.opens_kid", "Kid Mode"),
    accent: true,
  });
  rows.push({
    key: "alerts",
    label: ct("onboarding.deviceconfirm.row_alerts", "Your request alerts"),
    value: isParent
      ? ct("onboarding.deviceconfirm.alerts_ask", "We'll ask you next")
      : ct("onboarding.deviceconfirm.alerts_off", "Off here. They go to your own device"),
    dim: !isParent,
    accent: isParent,
  });
  profiles.forEach((p) => {
    const visible = isParent || !child || p.id === child.id;
    rows.push({
      key: `rem-${p.id}`,
      label: ct("onboarding.deviceconfirm.row_reminders", "{name}'s own reminders", { name: p.name }),
      value: isParent
        ? ct("onboarding.deviceconfirm.reminders_na", "Not offered here")
        : visible
          ? ct("onboarding.deviceconfirm.reminders_can", "{name} can turn these on", { name: p.name })
          : ct("onboarding.deviceconfirm.reminders_off", "Off"),
      dim: isParent || !visible,
    });
  });

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-sm p-6 max-h-[90vh] overflow-y-auto" data-testid="device-confirm">
        <div className="text-center mb-4">
          <div className="mb-1 flex justify-center"><Icon id="ic-celebrate" size={32} hue="pink" /></div>
          <div className="text-lg font-extrabold text-slate-900">{title}</div>
          <p className="text-xs text-slate-500 mt-1">{ct("onboarding.deviceconfirm.body", "Here's what that means on this device.")}</p>
        </div>
        <div className="bg-slate-50 border border-slate-100 rounded-2xl px-3 py-1 mb-3">
          {rows.map((r, idx) => (
            <div key={r.key} className={`flex items-center justify-between gap-3 py-2 ${idx < rows.length - 1 ? "border-b border-slate-100" : ""}`}>
              <span className={`text-xs font-medium ${r.dim ? "text-slate-300" : "text-slate-500"}`}>{r.label}</span>
              <span className={`text-xs font-medium text-right ${r.accent ? "text-teal-600" : r.dim ? "text-slate-300" : "text-slate-600"}`}>{r.value}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-400 leading-snug mb-3">
          {isParent
            ? ct("onboarding.deviceconfirm.foot_parent", "We'll ask for your PIN next, so only a grown-up can set this device up that way. Change any of it later in Parent Mode → Kids.")
            : ct("onboarding.deviceconfirm.foot", "Change any of it later in Parent Mode → Kids.")}
        </p>
        <button className="w-full bg-slate-900 text-white font-bold text-sm rounded-lg py-3" data-testid="device-confirm-save" onClick={onConfirm}>
          {ct("onboarding.deviceconfirm.confirm", "Looks right")}
        </button>
        <button className="w-full text-slate-500 font-semibold text-sm py-2" data-testid="device-confirm-back" onClick={onBack}>
          {ct("onboarding.deviceconfirm.back", "Pick someone else")}
        </button>
      </div>
    </div>
  );
}

// A child added AFTER this device was set up (Alan, 30 Aug 2026). Until now they simply
// appeared: the setup flow runs once, at join, and nothing re-asked. On a device that said
// it belonged to one child, a new sibling turning up on it is exactly the thing the flow
// was meant to prevent — so this asks once, and either answer records that the device has
// now seen them.
export function NewChildVisibilityPrompt({ child, roleLabel, onKeep, onShow }) {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-sm p-6 text-center" data-testid="new-child-prompt">
        <div className="mb-1 flex justify-center"><Icon id="ic-help" size={32} /></div>
        <div className="text-lg font-extrabold text-slate-900 mb-1">
          {ct("onboarding.newchild.heading", "{name} has joined the household", { name: child.name })}
        </div>
        <p className="text-xs text-slate-500 leading-relaxed mb-4">
          {ct("onboarding.newchild.body", "This device is set up as {role}. Should {name} show here too?", { role: roleLabel, name: child.name })}
        </p>
        <button className="w-full bg-slate-900 text-white font-bold text-sm rounded-lg py-3 mb-2" data-testid="new-child-keep" onClick={onKeep}>
          {ct("onboarding.newchild.keep", "Leave this device as it is")}
        </button>
        <button className="w-full bg-white border border-slate-200 text-slate-600 font-bold text-sm rounded-xl py-3" data-testid="new-child-show" onClick={onShow}>
          {ct("onboarding.newchild.show", "Show {name} here as well", { name: child.name })}
        </button>
      </div>
    </div>
  );
}

// Screen 2 is the existing PinPad component, unchanged — App.jsx renders it directly with
// its own submit handler for this flow. Its built-in "Cancel" button doubles as "back to
// screen 1" here, since App.jsx wires onCancel back to the intro screen rather than
// abandoning the flow outright.

// Screen 3 — per-child visibility + notifications, both defaulting on. Reuses the exact
// toggle visuals ProfilesTab already uses for "Show {name} on this device" (same green
// pill) so this feels like an early look at a control that lives permanently in Parent
// Mode → Kids, not a one-off flow-only widget.
//
// Notifications is forced off and disabled the moment a child is hidden (onToggleVisibility
// in App.jsx does the actual coupling) — a kid who isn't shown on this device can't
// independently have notifications on for it either. Showing them again does NOT restore
// notifications automatically; a parent has to deliberately flip that back on.
export function DeviceKidsToggleScreen({ profiles, hiddenIds, mutedIds, onToggleVisibility, onToggleMute, onFinish }) {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-sm p-6 max-h-[90vh] overflow-y-auto">
        <div className="text-xl font-extrabold text-slate-900 text-center mb-4">{ct("onboarding.devicekids.heading", "Choose what shows here")}</div>
        <div className="flex flex-col gap-2.5 mb-5">
          {profiles.map((p) => {
            const visible = !hiddenIds.includes(p.id);
            const notify = !mutedIds.includes(p.id);
            return (
              <div key={p.id} className="bg-slate-50 rounded-2xl p-3 border border-slate-100">
                <div className="flex items-center gap-2.5 mb-2.5">
                  <ChildMark profile={p} size="sm" />
                  <div className="font-semibold text-slate-800 text-sm flex-1 truncate">{p.name}</div>
                </div>
                <div className="flex items-center justify-between py-1">
                  <span className="text-xs font-medium text-slate-500">{ct("onboarding.show_on_device", "Show on this device")}</span>
                  <button type="button" onClick={() => onToggleVisibility(p.id)} aria-pressed={visible}
                    title={visible ? "Visible on this device — tap to hide" : "Hidden on this device — tap to show"}
                    className={`flex-shrink-0 inline-block w-9 h-5 rounded-full relative transition-colors ${visible ? "bg-emerald-500" : "bg-slate-200"}`}>
                    <span className={`absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${visible ? "translate-x-4" : "translate-x-0"}`} />
                  </button>
                </div>
                <div className="flex items-center justify-between py-1">
                  <span className={`text-xs font-medium ${visible ? "text-slate-500" : "text-slate-300"}`}>{ct("onboarding.notifications_label", "Notifications")}</span>
                  <button type="button" disabled={!visible} onClick={() => onToggleMute(p.id)} aria-pressed={notify}
                    title={!visible ? "Turned off automatically — this child is hidden on this device" : notify ? "Notifications on for this child on this device — tap to turn off" : "Notifications off for this child on this device — tap to turn on"}
                    className={`flex-shrink-0 inline-block w-9 h-5 rounded-full relative transition-colors ${notify && visible ? "bg-emerald-500" : "bg-slate-200"} ${!visible ? "opacity-50 cursor-not-allowed" : ""}`}>
                    <span className={`absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${notify && visible ? "translate-x-4" : "translate-x-0"}`} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        <button className="w-full bg-slate-900 text-white font-bold text-sm rounded-lg py-3" onClick={onFinish}>{ct("onboarding.devicekids.save_button", "Save & continue")}</button>
        <p className="text-xs text-slate-400 text-center mt-2">{ct("onboarding.devicekids.footnote", "You can change this any time in Parent Mode → Kids.")}</p>
      </div>
    </div>
  );
}
